import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFormik } from 'formik'

import { TwoFactorStatus } from '@/enums/two-factor-status.enum'
import { ApplicationState } from '@/state/root.models'
import {
  resetTwoFactorAction,
  setTwoFactorVerifiedAction,
  usersSelector,
} from '@/state/user/slices/index.slices'

import { validateCodesThunk } from '@/state/user/thunks/validate-code.thunk'
import { disableCodesThunk } from '@/state/user/thunks/disable-code.thunk'
import { initialValuesTwoFactor } from './schema'

import useActions from './use-actions.hook'
import { useAppDispatch } from '@/state'

const useTwoFactor = () => {
  const dispatch = useAppDispatch()

  const [activeStep, setActiveStep] = useState(1)

  const [section, setSection] = useState('main')

  const { loggedUser } = useSelector(({ auth }: ApplicationState) => auth)

  const { twoFactorVerified, backupCodes } = useSelector(usersSelector)

  const { getSecretCode, getBackupCodes, createBackupCodes } = useActions()

  const formikTwoFactor = useFormik({
    initialValues: initialValuesTwoFactor,
    onSubmit: () => {},
    enableReinitialize: true,
  })

  const setTwoFactorVerified = (status: TwoFactorStatus) => {
    dispatch(setTwoFactorVerifiedAction(status))
    if (status === TwoFactorStatus.INITIAL) {
      formikTwoFactor.resetForm()
    }
  }

  const handleShowSection = (value: string) => {
    setSection(value)
  }

  const handleHideSection = () => {
    setSection('main')
    setActiveStep(1)
    setTwoFactorVerified(TwoFactorStatus.INITIAL)
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleStep = (step: number) => {
    setActiveStep(step)
    setTwoFactorVerified(TwoFactorStatus.INITIAL)
  }

  const handleGetSecretCode = () => {
    getSecretCode({
      id: Number(loggedUser.id),
      userName: loggedUser.username,
      role: loggedUser.role,
    })
  }

  const resetTwoFactorState = () => {
    dispatch(resetTwoFactorAction())
    formikTwoFactor.resetForm()
  }

  const handleOnSubmit = () => {
    if (loggedUser.twoFactorAuthActive) {
      dispatch(
        disableCodesThunk({
          playerId: loggedUser.id,
          code: formikTwoFactor.values.twoFactorCode,
        }),
      )
    } else {
      dispatch(
        validateCodesThunk({
          playerId: loggedUser.id,
          code: formikTwoFactor.values.twoFactorCode,
        }),
      )
    }
  }

  const handleGenerateBackupCodes = () => {
    createBackupCodes({
      id: Number(loggedUser.id),
      userName: loggedUser.username,
      role: loggedUser.role,
    })
  }

  const handleGetBackupCodes = () => {
    getBackupCodes({
      id: Number(loggedUser.id),
      userName: loggedUser.username,
      role: loggedUser.role,
    })
  }

  useEffect(() => {
    if (loggedUser?.firstName?.length > 0) {
      resetTwoFactorState()
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (loggedUser.twoFactorAuthActive && activeStep === 1) {
      setActiveStep(4)
      handleHideSection()
      handleGetBackupCodes()
    } else {
      handleHideSection()
    }
  }, [loggedUser.twoFactorAuthActive]) // eslint-disable-line

  return {
    loggedUser,
    formikTwoFactor,
    activeStep,
    twoFactorVerified,
    backupCodes,
    section,
    handleBack,
    handleNext,
    handleStep,
    handleGetSecretCode,
    handleShowSection,
    handleHideSection,
    handleOnSubmit,
    setTwoFactorVerified,
    handleGenerateBackupCodes,
  }
}

export default useTwoFactor
